import React from 'react';
import ReactMarkdown, { ExtraProps, Options, Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

/**
 * Tailwind-based markdown component for rendering plain text with HTML elements
 * Uses https://github.com/remarkjs/react-markdown
 *
 * This component renders markdown using plain HTML elements,
 * allowing full control over styling via Tailwind classes passed from parent components.
 */

interface MarkdownTextTailwindProps extends Exclude<Options, 'components'> {
  className?: string;
  components?: Partial<Components>;
}

function MarkdownTextTailwind(props: MarkdownTextTailwindProps): React.ReactElement {
  const { children, className, components: customComponents, ...rest } = props;

  const transformedChildren =
    typeof children === 'string'
      ? children
          // Replace escaped newlines with actual newlines
          .replace(/\\n/g, '\n')
          // Replace underscores with <u> tags
          .replace(/__(.*?)__/g, '<u>$1</u>')
      : children;

  const defaultComponents: Partial<Components> = {
    p(props: React.HTMLAttributes<HTMLParagraphElement> & ExtraProps) {
      const { node, ...pRest } = props;
      return <p {...pRest} />;
    },
    a(props: React.HTMLAttributes<HTMLAnchorElement> & ExtraProps) {
      const { node, ...aRest } = props;
      return (
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="inline underline text-blue-600 hover:text-blue-800"
          {...aRest}
        />
      );
    },
    strong(props: React.HTMLAttributes<HTMLElement> & ExtraProps) {
      const { node, ...strongRest } = props;
      return <strong className="font-bold" {...strongRest} />;
    },
    em(props: React.HTMLAttributes<HTMLElement> & ExtraProps) {
      const { node, ...emRest } = props;
      return <em className="italic" {...emRest} />;
    },
    br: 'br',
  };

  // Merge custom components with defaults
  const components = { ...defaultComponents, ...customComponents };

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
        {...rest}
      >
        {transformedChildren}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownTextTailwind;
